import { FieldType } from "../../type-system/FieldType";
import { shopifyCurrencyCodeEnumOptions } from "./constants";
import {
  ShopifyCatalogKey,
  ShopifyPriceListKey,
  ShopifyPriceListPriceKey,
  ShopifyPriceListPriceToPriceListKey,
  ShopifyPriceListToCatalogKey,
  ShopifyPriceListToPriceListPriceKey,
  ShopifyPriceListToQuantityPriceBreakKey,
  ShopifyPriceListToShopKey,
  ShopifyQuantityPriceBreakKey,
  ShopifyQuantityPriceBreakToPriceListKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyPriceListKey,
  "2024-04": {
    name: "Shopify Price List",
    graphQLTypename: "PriceList",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Catalog",
        fieldType: FieldType.BelongsTo,
        key: ShopifyPriceListToCatalogKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "catalog", graphQLSelection: "id" } },
        configuration: {
          relatedModelKey: ShopifyCatalogKey,
        },
      },
      {
        name: "Currency",
        fieldType: FieldType.Enum,
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: shopifyCurrencyCodeEnumOptions,
        },
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "currency", isShopifyEnum: true } },
      },
      {
        name: "Fixed Prices Count",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "fixedPricesCount" } },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "name" } },
      },
      {
        name: "Parent",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "parent",
            graphQLSelection: `
            adjustment {
              type
              value
            }
            settings {
              compareAtMode
            }`,
          },
        },
      },
      {
        name: "Prices",
        fieldType: FieldType.HasMany,
        key: ShopifyPriceListToPriceListPriceKey,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyPriceListPriceKey,
          inverseFieldKey: ShopifyPriceListPriceToPriceListKey,
        },
      },
      {
        name: "Quantity Price Breaks",
        fieldType: FieldType.HasMany,
        key: ShopifyPriceListToQuantityPriceBreakKey,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyQuantityPriceBreakKey,
          inverseFieldKey: ShopifyQuantityPriceBreakToPriceListKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyPriceListToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "priceLists",
      filters: {},
    },
  },
};

export const ShopifyPriceList = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
